import * as ENGINE from '@gnsx/genesys.js';

import type { DropPayload } from '@/editor/core/dragDropTypes';
import type { IAssetFile } from '@/editor/store/models';
import type * as THREE from 'three';

import { requestViewportRender, ViewportRenderReason } from '@/editor/components/viewport/canvas/viewportRenderScheduler';
import { nameHelpers } from '@/editor/core';
import { type AssetHandler, AssetType } from '@/editor/core/assetTypes';
import { reportEditorHookError } from '@/editor/core/editorHookErrorReporter';
import { editorScene } from '@/editor/store';
import { editorStore } from '@/editor/store/editorStore';
import { applyDefaultOutlinerPlacementIntent, captureDefaultOutlinerPlacementIntent } from '@/editor/store/sceneStore';


export class VfxHandler implements AssetHandler {
  private actor: ENGINE.Actor | null = null;

  /** Creates an actor with a VFXComponent configured with the VFX asset path */
  private async addActor(asset: IAssetFile, worldPosition: THREE.Vector3): Promise<ENGINE.Actor | null> {
    const world = editorScene.world;
    if (!world) {
      console.error('No world instance found');
      return null;
    }

    const outlinerPlacement = captureDefaultOutlinerPlacementIntent();

    try {
      // Create a VFXComponent with the vfxPath set
      const vfxComponent = ENGINE.VFXComponent.create({ vfxPath: asset.path });

      // Create an actor and set the VFXComponent as its root
      const actor = ENGINE.Actor.create({ position: worldPosition });
      actor.setRootComponent(vfxComponent, false);

      // Add to world
      try {
        world.addActors(actor);
        requestViewportRender(ViewportRenderReason.SceneStateChanged);
      } catch (error) {
        reportEditorHookError({
          hook: 'onEditorAddToWorld',
          context: 'VfxHandler.addActor',
          target: actor,
          error,
        });
        return null;
      }
      editorScene.updateOutliner();
      const root = editorScene.outliner.rootNode ?? undefined;
      const unique = nameHelpers.assignUniqueActorDisplayName(actor, root, { omitNodeId: actor.uuid, assetPathHint: asset.path });
      editorScene.outliner.getNodeById(actor.uuid)?.setName(unique);
      applyDefaultOutlinerPlacementIntent([actor.uuid], outlinerPlacement);
      editorScene.selectObject(actor);
      requestAnimationFrame(() => {
        editorScene.outliner.scrollToSelected();
      });

      console.log(`Spawned VFX Actor at ${worldPosition.toArray().join(', ')}: ${asset.path}`);
      return actor;
    } catch (error) {
      console.error('Failed to spawn VFX Actor:', error);
      return null;
    }
  }

  async handleOpen(asset: IAssetFile): Promise<void> {
    // Open VFX editor dialog
    editorStore.openVFXEditor(asset.path);
  }

  async handleSelect(asset: IAssetFile): Promise<void> {
    // Just select the asset in the browser
    editorStore.setSelectedAssetItem(asset);
  }

  getTargetObject(): unknown {
    return this.actor;
  }

  getAssetType(): AssetType {
    return AssetType.VFX;
  }

  async handleDragEnter(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor = await this.addActor(payload.payload, worldPosition);
  }

  async handleDragOver(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor?.rootComponent.setWorldPosition(worldPosition);
    if (this.actor && this.actor === editorScene.selectedObject?.asActor) {
      editorScene.selectedObject.setDisplayPosition(worldPosition);
    }
  }

  async handleDragCancel(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    editorScene.selectObject(null);
    this.actor?.destroy();
    editorScene.updateOutliner();
  }

  async handleDrop(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor = null;
  }
}

